import React, { useState, useEffect, useRef } from "react"
import { graphql } from 'gatsby'
import Flink from '~/components/shared/flink'
import { useSwipeable } from 'react-swipeable'
import { Seo } from '~/components/shared/seo'
import useSearchHook from '~/utils/use-search'

import { useTheme } from '~/contexts/ThemeContext'
import useAnalytics from '~/utils/analytics'
import { isStag, imgSize, altText, notBlank, compact, jnstyls } from '~/utils/helpers'

import FavProd from '~/components/shared/fav-prod'
import IconSearch from '~/components/icons/search'

import { aria_btn } from "~/styles/app.module.scss"
import * as tiless from "~/styles/tiles/tile.module.scss"
import * as tsstyles from "~/styles/pdp/title.module.scss"
import * as sstyles from "~/styles/panels/search.module.scss"
import * as istyle from "~/styles/shared/img_grid.module.scss";
import * as styles from "~/styles/pages/search_page.module.scss"


const SearchPage = ({ data }) => {
  const { analytics, setAnalytics, setBread, gender, setGender } = useTheme()
  const content = data?.contentPage?.data || {}
  const { ga_search_click, elev_search, elev_select } = useAnalytics()

  let initgend = gender?.srGender?.slice(0,1) || null

  const [searchData, setSearchData] = useState(null)
  const [prodResults, setProdResults] = useState([])
  const [searchType, setSearchType] = useState(initgend)
  const [currSort, setCurrSort] = useState('best')
  const [currSizes, setCurrSizes] = useState([])
  const [sizes, setSizes] = useState(null)
  const [suggests, setSuggests] = useState([])
  const [dontGuess, setDontGuess] = useState(false)
  const [searchTest, setSearchTest] = useState(false)

  const searchApi = useSearchHook({searchType, currSort, currSizes, setSuggests, dontGuess})
  const { getSearchData, searchProds, filterSizes, getSearch, saveSearch} = searchApi

  const inptRef = useRef(null)
  const wrRef = useRef(null)
  const valsRef = useRef(null)
  const searchTimeout = useRef(null)

  const searchClear = () => {
    setProdResults([])
    valsRef.current = null
    setCurrSort('best')
    setCurrSizes([])
    setSizes(null)
    setSuggests([])
    setDontGuess(false)
    localStorage.removeItem('search')
    inptRef.current.value = ''
  }

  const debounce = (func, delay) => {
    return (...args) => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
      searchTimeout.current = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const handleSearch = (e) => {
    if (!searchData) return

    const val = inptRef.current.value.toLowerCase()

    if ( val === '' ) {
      searchClear()
      return
    }
    if (!!e && dontGuess) {
      setDontGuess(false)
      return
    }

    if ( val.length < 3 ) return

    let gendvals = compact(val.split(' '))
    const gends = ['men', 'mens', "men's", 'women', 'womens', "women's"]
    let has_gend = gendvals.find(x => gends.includes(x.toLowerCase()))
    let search_gend = has_gend?.toLowerCase()?.slice(0,1)
    if (has_gend && search_gend !== searchType) {
      setSearchType(search_gend)
      return
    }
    
    const prods = searchProds({val})
    if (!prods) return

    setProdResults(prods.flat())
    if (localStorage.getItem('csearch')) parseSizes(prods)
    do_saving()
    const anaprods = prods.flat()
    debounce(elev_search, 2000)(val, anaprods.slice(0,36))
  }

  const parseSizes = (prods) => {
    let all_sizes = [...new Set( prods.flat().flatMap(product => Object.keys(product.variants)) )]
    all_sizes = orderSizes(all_sizes).filter(x => x.length > 0)

    let avail_sizes = prods.flat().flatMap(product => {
      return compact(Object.keys(product.variants).map(k => !!product.variants[k] ? k : null))
    })
    avail_sizes = [...new Set(compact(avail_sizes))]
    setSizes({all: all_sizes, avail: avail_sizes})
  }

  const orderSizes = (sizes) => {
    const size_order = ['xxs','2s','xs','s','m','l','xl','xxl','2x','xxxl','3x','os','one size']
    let str_sizes = sizes.filter(x => !Number.isInteger(Number(x)) && !x.includes('.'))
    str_sizes = str_sizes.sort((a, b) => size_order.indexOf(a.toLowerCase()) - size_order.indexOf(b.toLowerCase()) )

    let int_sizes = sizes.filter(x => Number.isInteger(Number(x)) || x.includes('.'))
    int_sizes = int_sizes.sort((a, b) => parseFloat(a) - parseFloat(b))

    let shoe_sizes = int_sizes.filter(x => x < 20)
    int_sizes = int_sizes.filter(x => !shoe_sizes.includes(x))
    return [str_sizes, int_sizes, shoe_sizes]
  }

  const do_saving = async () => {
    const val = inptRef.current.value
    if ( val === '' ) return

    const obj = {
      prodResults: prodResults,
      searchType: searchType,
      currSort,
      suggests,
      dontGuess
    }
    const vals_arr = compact(val.split(' '))
    const has_saved = await saveSearch(obj, vals_arr)
    return has_saved
  }

  const load_search = () => {
    const saved = getSearch()
    inptRef.current.value = saved.val_arr.join(' ')

    const is_pl = saved.prodResults.flat().find(x => x.hasOwnProperty('feats'))
    const savedProds = is_pl ? [] : saved.prodResults.flat()

    setSearchType(saved.searchType)
    setCurrSort(saved.currSort || 'best')
    setDontGuess(saved.dontGuess || false)
    handleSearch()
  }

  const gaClick = (product=false, isPopular=false, index=false) => {
    const val = inptRef.current.value
    const term = (isPopular && inptRef.current.value === '') ? isPopular.toLowerCase() : inptRef.current.value
    const type = searchType === null ? 'all' : searchType === 'm' ? 'men' : searchType === 'w' ? 'women' : searchType

    ga_search_click(term, type, !!isPopular)
    if (product) {
      setAnalytics({...analytics, list: 'search results'})
      elev_select(product, index, 'search results')
    }
  }

  const get_search_data = async () => {
    const data = await getSearchData({})
    setSearchData(data)
  }

  const calloutText = (id) => {
    return searchData.callouts.find(x => x.id === id)?.data || {}
  }


  useEffect(() => {
    setBread({handle: 'search', title: 'Search Results'})
    if (localStorage.getItem('csearch')) setSearchTest(true)

    if (!searchData) get_search_data()
    if (inptRef.current) {
      inptRef.current.focus();
    }
    if (wrRef.current) wrRef.current.style.opacity = 1

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!searchData) return
    if (getSearch()) {
      load_search()
      return
    } 
    let gend = gender?.srGender?.slice(0,1) || null
    setSearchType(gend)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchData])

  useEffect(() => {
    if (!searchData) return
    handleSearch()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType, currSort, dontGuess])

  useEffect(() => {
    if (!searchData) return
    if (!sizes) return
    if (currSizes.length < 1) {
      handleSearch()
      return
    }
    setProdResults( filterSizes({results: prodResults, val: inptRef.current.value}).flat() )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currSizes])

 
  return (
    <section 
      className={styles.search_page} 
      ref={wrRef}
    >
      <div className={[sstyles.head, styles.head].join(' ')}>
        <div className={sstyles.inputwr}>
          <input type="search"
              name="q"
              placeholder="Search our store"
              className={sstyles.search_inpt}
              onChange={(e)=>{handleSearch(e)}}
              ref={inptRef}
           />
          <div className={sstyles.search_btn}>
            <IconSearch icon_classes={''} />
          </div>
          <div className={sstyles.search_gender} >
            <button 
              type="button"
              className={`${aria_btn} ${sstyles.gender_btn}`} 
              aria-label="Search all products" 
              aria-current={!searchData || searchType === null ? 'true' : 'false'}
              onClick={(e)=>{setSearchType(null)}}
            >
              All
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${sstyles.gender_btn}`} 
              aria-label="Search mens products" 
              aria-current={searchData && searchType === 'm' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('m')}}
            >
              Men's
            </button>
            <button 
              type="button"
              className={`${aria_btn} ${sstyles.gender_btn}`} 
              aria-label="Search womens products" 
              aria-current={searchData && searchType === 'w' ? 'true' : 'false'}
              onClick={(e)=>{setSearchType('w')}}
            >
              Women's
            </button>
            <div className={sstyles.search_extra}>
              {inptRef?.current?.value != '' && (
                <button
                  type='button'
                  className={sstyles.clear}
                  onClick={(e) => searchClear()}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {searchTest && suggests.length > 0 && (
        <div className={sstyles.suggestswr}>
          
          {(suggests && !dontGuess) ? (
            <>
              <div className={sstyles.suggests}>
                <div className={sstyles.suggestlab}>
                  Showing results for:
                </div>
                {suggests.map((term,index) =>
                  <div key={`${term}_${index}`} className={sstyles.suggest}>{term}</div>
                )}
              </div>
              <div className={sstyles.suggests}>
                <div className={sstyles.suggestlab}>
                  Search instead for:
                </div>
                <button
                  type='button'
                  className={sstyles.suggestbtn}
                  onClick={(e) => setDontGuess(true)}
                >
                  {inptRef.current.value}
                </button>
              </div>
            </>
          ) : (
            <div className={sstyles.suggests}>
              <div className={sstyles.suggestlab}>
                Search instead for:
              </div>
              <button
                type='button'
                className={sstyles.suggestbtn}
                onClick={(e) => setDontGuess(false)}
              >
                {suggests.join(' ')}
              </button>
            </div>
          )}
        </div>
      )}
      <div className={styles.resultswr}> 
        {(prodResults.length < 1) && (
          <>
            <div className={styles.tiles}>
              {(inptRef.current && notBlank(inptRef.current.value)) && (
                <div className={[sstyles.restitle, styles.prods_head].join(' ')} style={{paddingBottom: '1.5em'}}>
                  <div className={[sstyles.title, styles.title].join(' ')}>No Products Found</div>
                </div>
              )}
            </div>
            <div className={styles.poptiles}>
              {(!searchData || (!searchType || searchType === 'm')) && content?.mens_links?.length > 0 && (
                <>
                  <div className={[sstyles.restitle, styles.prods_head].join(' ')}>
                    <div className={[sstyles.title, styles.title].join(' ')}>Popular Men's Searches</div>
                  </div>
                  {content.mens_links.map(link => 
                    <div 
                      key={link.link_text}
                      className={[tiless.tile, tiless.page_tile].join(' ')}
                    >
                      <div className={[tiless.images, styles.pop_images].join(' ')}>
                        {(link.image) && (
                          <Flink 
                            href={link.url} 
                            className={[tiless.tile_link, tiless.tile_title].join(' ')} 
                            aria-label={`Popular Search: ${link.link_text}`}
                            onClick={(e)=>{
                              gaClick(false,link.link_text)
                            }} 
                          >
                            <img 
                              src={imgSize(link.image,700)}  
                              alt={`Popular Search: ${link.link_text}`}
                            />
                          </Flink>
                        )}
                      </div>
                      <div className={tiless.infos} tile-infos="">
                        <div className={[tiless.info, styles.t_title].join(' ')}>
                          <Flink 
                            className={[tiless.tile_link, tiless.tile_title].join(' ')} 
                            href={link.url} 
                            onClick={(e)=>{
                              gaClick(false,link.link_text)
                            }} 
                          >
                            {link.link_text}
                          </Flink>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              {(!searchData || (!searchType || searchType === 'w')) && content?.womens_links?.length > 0 && (
                <>
                  <div className={[sstyles.restitle, styles.prods_head].join(' ')}>
                    <div className={[sstyles.title, styles.title].join(' ')}>Popular Women's Searches</div>
                  </div>
                  {content.womens_links.map(link => 
                    <div 
                      key={link.link_text}
                      className={[tiless.tile, tiless.page_tile].join(' ')}
                    >
                      <div className={[tiless.images, styles.pop_images].join(' ')}>
                        {(link.image) && (
                          <Flink 
                            href={link.url} 
                            className={[tiless.tile_link, tiless.tile_title].join(' ')} 
                            aria-label={`Popular Search: ${link.link_text}`}
                            onClick={(e)=>{
                              gaClick(false,link.link_text)
                            }} 
                          >
                            <img 
                              src={imgSize(link.image,700)}  
                              alt={`Popular Search: ${link.link_text}`}
                            />
                          </Flink>
                        )}
                      </div>
                      <div className={tiless.infos} tile-infos="">
                        <div className={[tiless.info, styles.t_title].join(' ')}>
                          <Flink 
                            className={[tiless.tile_link, tiless.tile_title].join(' ')} 
                            href={link.url} 
                            onClick={(e)=>{
                              gaClick(false,link.link_text)
                            }} 
                          >
                            {link.link_text}
                          </Flink>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      
        {(prodResults.length > 0) && (
          <div className={styles.tiles}>
            <Sorts 
              currSort={currSort} 
              setCurrSort={setCurrSort} 
              prodResults={prodResults}
              sizes={sizes} 
              currSizes={currSizes} 
              setCurrSizes={setCurrSizes}
            />
          
            {prodResults.length > 0 && prodResults.map( (product, index) => 
              <SearchTile 
                key={`${product.id}-tile-${index}`} 
                product={product} 
                index={index} 
                searchType={searchType} 
                prodResults={prodResults}
                sizes={sizes} 
              />
            )}
          </div>
        )}
      </div>
    </section>
  )
}

const Sorts = ({currSort, setCurrSort, prodResults, sizes, currSizes, setCurrSizes}) => {

  const countText = () => {
    const num = prodResults.flat().length;
    return `${num} result${num !== 1 ? 's' : ''}`
  }

  const toggleSize = (size) => {
    if (currSizes.includes(size)) {
      setCurrSizes(currSizes.filter(x => x !== size))
      return
    }
    setCurrSizes([...new Set([...currSizes, size])])
  }


  return (
    <div className={[sstyles.restitle, styles.prods_head].join(' ')}>
      <div className={[sstyles.title, styles.title].join(' ')}>{countText()}</div>
      <div className={sstyles.count_sorts}>
        <div className={sstyles.sorts}>
          <button
            type='button'
            className={sstyles.sort}
            aria-current={(currSort === 'best').toString()}
            onClick={(e) => setCurrSort('best')}
          >
            Best Sellers
          </button>
          <button
            type='button'
            className={sstyles.sort}
            aria-current={(currSort === 'rec').toString()}
            onClick={(e) => setCurrSort('rec')}
          >
            Recent
          </button>
          <button
            type='button'
            className={sstyles.sort}
            aria-current={(currSort === 'asc').toString()}
            onClick={(e) => setCurrSort('asc')}
          >
            Price
          </button>
        </div>
      </div>
      {(!!sizes && (prodResults.length > 1 || !!sizes)) && (
        <div className={sstyles.navsizeswr}>
          {sizes.all.map((group, sindx) =>
            <div key={`sizegrp_${sindx}`} className={sstyles.navsizes}>
              {group.map(size =>
                <button
                  key={`navsize_${size}`} 
                  type='button'
                  className={sstyles.navsize}
                  disabled={sizes.avail.includes(size) ? undefined : 'disabled'}
                  onClick={()=>toggleSize(size)}
                  aria-selected={(currSizes.includes(size)).toString()}
                >
                  {size}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const SearchTile = ({product, index, searchType, prodResults, sizes}) => {
  const { analytics, setAnalytics, isTouches, customer } = useTheme()
  const { ga_search_click, elev_search, elev_select } = useAnalytics()

  const [curr, setCurr] = useState(0)

  const gaClick = (product=false, index=false) => {
    const term = document.querySelector('[name="q"]').value

    const type = searchType === null ? 'all' : searchType === 'm' ? 'men' : searchType === 'w' ? 'women' : searchType

    ga_search_click(term, type, false)
    elev_search(term, prodResults)
    if (product) {
      setAnalytics({...analytics, list: 'search results'})
      elev_select(product, index, 'search results')
    }
  }

  let media = product.media || []
  if (product.gender === 'w') {
    media = media.reverse()
  }

  const swipers = useSwipeable({
    onSwiping: (e) => {
      if (!isTouches) return
    },
    onSwipedLeft: (e) => {
      if (!isTouches ) return
      img_forw()
    },
    onSwipedRight: (e) => {
      if (!isTouches) return
      img_back()
    },
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: true,
    delta: {
      up: 1000,
      down: 1000,
    },
  })

  const img_forw = () => {
    const cnt = product.media.length
    if (cnt < 2) return
    setCurr(curr === 0 ? 1 : 0)
  }

  const img_back = () => {
    const cnt = product.media.length
    if (cnt < 2) return
    setCurr(curr === 0 ? 1 : 0)
  }

  const is_gift = product.handle.includes('gift')

  let alt_cuts = false

  return (
    <div 
      className={[tiless.tile, tiless.page_tile].join(' ')}
      id={product.id}
    >
      {(media.length > 0) && (
        <>
          {(media.length > 1) ? (
            <Flink
              href={`/products/${product.handle}`} 
              className={[tiless.images, styles.images, tiless.tile_link, isTouches && curr > 0 ? styles.mswipe : ''].join(' ')} 
              onClick={(e)=>{
                gaClick(product, false, index)
              }}
              {...swipers}
            >
              {(media.length > 0) && (
                <>
                  {media.map((image, imgind) => 
                    <img 
                      key={`${product.id}-img${imgind}`} 
                      className={styles.img} 
                      src={imgSize(image,700)} 
                      alt={imgind === 0 ? altText(null, product.title) : `Different view of ${product.title}`}
                    />
                  )}
                  <div className={[istyle.indicate, styles.indicate].join(' ')}>
                    {media.map((x,indidx) => 
                      <div 
                        key={`${product.id}-dot${indidx}`} 
                        className={[istyle.ind_dot, indidx === 0 ? istyle.curr : ''].join(' ')} 
                        inds='' 
                      />
                    )}
                  </div>
                </>
              )}
              {alt_cuts && (
                <div className={tiless.alts}>
                  {alt_cuts.opts.length} {alt_cuts.type}s
                </div>
              )}
            </Flink>
          ) : (
            <Flink
              href={`/products/${product.handle}`} 
              className={[tiless.images, styles.images, tiless.tile_link].join(' ')} 
              onClick={(e)=>{
                gaClick(product, false, index)
              }}
            >
              {media.map((image, imgind) => 
                <img 
                  key={`${product.id}-img${imgind}`} 
                  className={styles.img} 
                  src={imgSize(image,700)} 
                  alt={imgind === 0 ? altText(null, product.title) : `Different view of ${product.title}`}
                />
              )}
              {alt_cuts && (
                <div className={tiless.alts}>
                  {alt_cuts.opts.length} {alt_cuts.type}s
                </div>
              )}
            </Flink>
          )}
        </>
      )}

      <div className={tiless.infos} tile-infos="">
        <div className={[tiless.info, styles.t_title].join(' ')}>
          <Flink 
            href={`/products/${product.handle}`} 
            className={[tiless.tile_link, tiless.tile_title].join(' ')} 
            onClick={(e)=>{
              gaClick(product, false, index)
            }}
          >
            {product.pl_name ? product.pl_name : product.title}
          </Flink>
          <div data-product-price="" className={tiless.price} data-price-wrapper="">
            {is_gift ? (
              <span>
                $25-100
              </span>
            ) : (
              <>${product.price}</>
            )}
          </div>
        </div>

        {(customer && localStorage.getItem('ward')) && (
          <FavProd prod_id={product.mid} tile={true} />
        )}

        {!is_gift && product?.color?.name && (
          <div className={[tiless.info, tiless.color].join(' ')} tile-color="">
            {product?.color?.name}
          </div>
        )}

        {!!sizes && (
          <div className={sstyles.variants}>
            {product.variants && Object.keys(product.variants).map(size => 
              <div
                key={`${product.mid}_var_${size}`} 
                className={sstyles.variant}
                disabled={!!product.variants[size] ? undefined : 'disabled'}
              >
                {size}
              </div>
            )}
          </div>
        )}

        <div className={jnstyls([sstyles.info, sstyles.infoextra])}>
          <div className={styles.colors}>
            {(product?.pl_colors && (product.pl_colors - 1) > 0) && (
              <>{product.pl_colors - 1} Other Color{(product.pl_colors  - 1) > 2 ? 's' : ''}</>
            )}
          </div>
          <div className={sstyles.callouts}>
            {(!!product.best) && (
              <div className={sstyles.callout}>
                Best Seller
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}


export default SearchPage


export const Head = () => (
  <Seo 
    title={'Search'}
    url={`/search`}
  />
)

export const pageQuery = graphql`
  {
    contentPage(type: {eq: "SearchPanel"}) {
      data
    }
  }
`;





